import React, { useState } from 'react';
import SplitText from "../../../common/SplitText";
import ContactModal from "../../../modal/ContactModal";
import { HiOutlineArrowSmRight } from "react-icons/hi";
import { IoCheckmarkCircle } from "react-icons/io5";

const premium = process.env.PUBLIC_URL + '/images/preminum.webp';
const mid_range = process.env.PUBLIC_URL + '/images/mid-range.webp';
const economy = process.env.PUBLIC_URL + '/images/economy.webp';

export default function Franchise() {
    const [activeIdx, setActiveIdx] = useState(1); // Default to Mid-Range (Most Popular)
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    const cartModels = [
        {
            id: 'economy',
            name: 'Economy Cart',
            price: '₹3 Lakhs',
            badge: '🚀 Starter Friendly',
            badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
            glowColor: 'rgba(52, 199, 89, 0.25)',
            themeGradient: 'from-[#34C759] to-[#00A859]',
            image: economy,
            tagline: 'Ideal for first-time entrepreneurs & college spots',
            description: 'A highly agile, budget-friendly mobile unit engineered for quick deployment in small neighborhood markets, bus terminals, and high-footfall local avenues.',
            specs: [
                'Daily Serving Capacity: 250+ Kulfis',
                'Compact High-Mobility Chassis',
                'Deep-Freeze Insulated Battery Tank',
                '0% Royalty & 100% Retained Profit'
            ]
        },
        {
            id: 'mid-range',
            name: 'Mid-Range Cart',
            price: '₹5 Lakhs',
            badge: '⭐ Most Popular',
            badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
            glowColor: 'rgba(168, 128, 255, 0.3)',
            themeGradient: 'from-[#A880FF] to-[#7B61FF]',
            image: mid_range,
            tagline: 'Best balance of high storage & heavy transit footfall',
            description: 'Our most popular commercial cart featuring heavy-duty suspension, illuminated brand canopy, digital menu mounts, and expanded multi-flavor storage for prime city spots.',
            specs: [
                'Daily Serving Capacity: 500+ Kulfis',
                'Illuminated LED Brand Canopy',
                'Integrated Sound & Digital Display Unit',
                '0% Royalty with Weekly Stock Logistics'
            ]
        },
        {
            id: 'premium',
            name: 'Premium Cart',
            price: '₹10 Lakhs',
            badge: '✨ Flagship Model',
            badgeColor: 'bg-rose-100 text-rose-800 border-rose-200',
            glowColor: 'rgba(250, 140, 166, 0.35)',
            themeGradient: 'from-[#FA8CA6] to-[#E85D75]',
            image: premium,
            tagline: 'Luxury multi-flavor station for tech parks & malls',
            description: 'A luxurious flagship mobile dessert station with high-grade stainless steel fabrication, ambient neon aesthetic, solar power backup, and maximum customer attraction.',
            specs: [
                'Daily Serving Capacity: 1,000+ Kulfis',
                'Solar Power + 12h Battery Inverter Backup',
                'Dual Temperature Commercial Freezer',
                'Priority Territory Exclusivity & Training'
            ]
        }
    ];

    const activeModel = cartModels[activeIdx];
    // Left card is previous, right card is next
    const leftIdx = (activeIdx + 2) % 3;
    const rightIdx = (activeIdx + 1) % 3;

    return (
        <div id='franchise-options' className='franchise-section mt-[80px] sm:mt-[120px] relative text-center px-[16px] sm:px-[24px] lg:px-[40px] max-w-[1440px] mx-auto scroll-mt-24'>
            {/* Header */}
            <div className='flex justify-center'>
                <SplitText
                    text="The Franchise Options"
                    tag="h2"
                    className='text-[#0F0200] text-[28px] sm:text-[40px] lg:text-[48px] xl:text-[54px] font-bold'
                    delay={30}
                    duration={0.6}
                    ease="power3.out"
                    splitType="words, chars"
                    from={{ opacity: 0, y: 30 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0.1}
                    rootMargin="-40px"
                    textAlign="center"
                />
            </div>
            
            <p className='text-[#475569] text-[15px] sm:text-[18px] font-normal mt-[10px] sm:mt-[14px] max-w-[680px] mx-auto leading-relaxed'>
                Select and explore our three turnkey cart models tailored for different investment levels and locations.
            </p>

            {/* Wireframe 3-Part Layout: Left Card, Center 3D Showcase & Content, Right Card */}
            <div className='mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 xl:gap-8 items-center'>
                
                {/* Left Card (Card 1) - Compact */}
                <div 
                    onClick={() => setActiveIdx(leftIdx)}
                    className='lg:col-span-3 bg-white rounded-[24px] p-5 sm:p-6 border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 cursor-pointer flex flex-col justify-between text-left group order-2 lg:order-1 h-auto self-center'
                >
                    <div className='flex items-center justify-between gap-2 mb-3'>
                        <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${cartModels[leftIdx].badgeColor}`}>
                            {cartModels[leftIdx].badge}
                        </span>
                        <span className='text-xs font-bold text-[#73C8BD]'>
                            {cartModels[leftIdx].price}
                        </span>
                    </div>

                    <div className='h-[105px] w-full flex items-center justify-center my-2'>
                        <img 
                            src={cartModels[leftIdx].image} 
                            alt={cartModels[leftIdx].name} 
                            className='max-h-full max-w-[140px] object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300'
                        />
                    </div>

                    <div className='mt-2'>
                        <h3 className='text-[#181F38] text-base xl:text-lg font-bold group-hover:text-[#73C8BD] transition-colors'>
                            {cartModels[leftIdx].name}
                        </h3>
                        <p className='text-[#64748B] text-xs mt-1 line-clamp-2 leading-relaxed'>
                            {cartModels[leftIdx].tagline}
                        </p>
                    </div>

                    <div className='mt-3.5 pt-2.5 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-[#73C8BD]'>
                        <span>Inspect 3D Model</span>
                        <span className='group-hover:translate-x-1 transition-transform'>→</span>
                    </div>
                </div>

                {/* Center Showcase Stage: Upper 3D Floating Cart Image + Lower Content Card */}
                <div className='lg:col-span-6 flex flex-col items-center justify-center relative order-1 lg:order-2'>
                    
                    {/* Ambient Glow Backdrop */}
                    <div 
                        className='absolute top-4 left-1/2 -translate-x-1/2 w-[300px] sm:w-[380px] h-[300px] sm:h-[380px] rounded-full blur-[90px] pointer-events-none transition-all duration-700'
                        style={{ backgroundColor: activeModel.glowColor }}
                    />

                    {/* UPPER: 3D Cart Image with Floating Levitation Animation & Cast Shadow */}
                    <div className='relative z-10 w-full flex flex-col items-center justify-center pt-1 pb-4'>
                        <div className='relative w-full max-w-[420px] sm:max-w-[480px] lg:max-w-[540px] h-[260px] sm:h-[320px] lg:h-[360px] xl:h-[390px] flex items-center justify-center'>
                            <img 
                                key={activeModel.id}
                                src={activeModel.image} 
                                alt={activeModel.name} 
                                className='max-h-full max-w-full object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.2)] animate-float-3d transition-all duration-500 scale-105'
                            />
                        </div>
                        {/* Dynamic Pulsing Cast Shadow */}
                        <div className='w-[200px] sm:w-[260px] lg:w-[300px] h-[16px] bg-black/20 rounded-[100%] blur-[8px] animate-shadow-pulse -mt-2 pointer-events-none' />
                    </div>

                    {/* LOWER: Clean Content Box with Specifications & CTA */}
                    <div className='relative z-10 w-full mt-2 text-left bg-white rounded-[26px] p-6 sm:p-7 border border-gray-100 shadow-card'>
                        <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
                            <span className={`text-xs font-bold px-3 py-1 rounded-full border ${activeModel.badgeColor}`}>
                                {activeModel.badge}
                            </span>
                            <span className='text-xs font-semibold text-[#64748B]'>
                                Turnkey Setup • 0% Royalty
                            </span>
                        </div>

                        <div className='flex flex-wrap items-baseline justify-between gap-2 mt-1'>
                            <h3 className='text-2xl sm:text-3xl font-extrabold text-[#181F38]'>
                                {activeModel.name}
                            </h3>
                            <span className='text-2xl sm:text-3xl font-extrabold text-[#73C8BD]'>
                                {activeModel.price}
                            </span>
                        </div>

                        <p className='text-[#475569] text-xs sm:text-sm leading-relaxed mt-2.5'>
                            {activeModel.description}
                        </p>

                        {/* Specs List */}
                        <div className='grid sm:grid-cols-2 gap-2 mt-4 pt-4 border-t border-gray-100'>
                            {activeModel.specs.map((spec, sIdx) => (
                                <div key={sIdx} className='flex items-center gap-2 text-xs text-[#181F38] font-medium'>
                                    <IoCheckmarkCircle className='text-[#73C8BD] text-sm shrink-0' />
                                    <span>{spec}</span>
                                </div>
                            ))}
                        </div>

                        {/* CTA Button */}
                        <div className='mt-5 flex flex-wrap items-center gap-3'>
                            <button
                                type='button'
                                onClick={() => setIsContactModalOpen(true)}
                                className='w-full sm:w-auto flex-1 bg-[#73C8BD] hover:bg-[#5db8ac] active:scale-95 text-white font-bold py-3.5 px-6 rounded-xl shadow-glow-teal hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base'
                            >
                                <span>Apply for {activeModel.name}</span>
                                <HiOutlineArrowSmRight className='text-xl' />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Card (Card 2) - Compact */}
                <div 
                    onClick={() => setActiveIdx(rightIdx)}
                    className='lg:col-span-3 bg-white rounded-[24px] p-5 sm:p-6 border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 cursor-pointer flex flex-col justify-between text-left group order-3 h-auto self-center'
                >
                    <div className='flex items-center justify-between gap-2 mb-3'>
                        <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${cartModels[rightIdx].badgeColor}`}>
                            {cartModels[rightIdx].badge}
                        </span>
                        <span className='text-xs font-bold text-[#73C8BD]'>
                            {cartModels[rightIdx].price}
                        </span>
                    </div>

                    <div className='h-[110px] w-full flex items-center justify-center my-2'>
                        <img 
                            src={cartModels[rightIdx].image} 
                            alt={cartModels[rightIdx].name} 
                            className='max-h-full max-w-[150px] object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300'
                        />
                    </div>

                    <div className='mt-2'>
                        <h3 className='text-[#181F38] text-lg font-bold group-hover:text-[#73C8BD] transition-colors'>
                            {cartModels[rightIdx].name}
                        </h3>
                        <p className='text-[#64748B] text-xs mt-1 line-clamp-2 leading-relaxed'>
                            {cartModels[rightIdx].tagline}
                        </p>
                    </div>

                    <div className='mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-[#73C8BD]'>
                        <span>Inspect 3D Model</span>
                        <span className='group-hover:translate-x-1 transition-transform'>→</span>
                    </div>
                </div>

            </div>

            <ContactModal 
                isOpen={isContactModalOpen} 
                onClose={() => setIsContactModalOpen(false)} 
            />
        </div>
    )
}

